"use client";

import { useCallback, useEffect, useState } from "react";

import type { DeveloperOptions } from "@/lib/dev/options";
import {
  getCustomEventName,
  loadDeveloperOptions,
  saveDeveloperOptions,
  setDeveloperOption,
} from "@/lib/dev/options";

/**
 * Hook to manage developer options.
 * Returns object with { devOptions, setDevOption, replaceAllDevOptions }.
 *
 * Automatically syncs across tabs via storage events and within the same tab via custom events.
 */
// Defaults match the production fallback in lib/dev/options. Keeping
// the initial state stable (not reading localStorage in the lazy
// initializer) ensures the server-rendered HTML matches the client's
// first render — the localStorage-backed value is folded in via the
// effect below, after hydration completes.
const DEFAULT_DEV_OPTIONS: DeveloperOptions = { developerMode: false, version: 1 };

export default function useDeveloperOptions() {
  const [devOptions, setDevOptionsState] = useState<DeveloperOptions>(DEFAULT_DEV_OPTIONS);

  useEffect(() => {
    setDevOptionsState(loadDeveloperOptions());

    const handleStorageChange = (e: StorageEvent) => {
      // Only reload if our specific key changed (cross-tab sync)
      if (e.key === "nombox-developer-options") {
        setDevOptionsState(loadDeveloperOptions());
      }
    };

    const handleCustomEvent = () => {
      // Same-tab sync
      setDevOptionsState(loadDeveloperOptions());
    };

    const eventName = getCustomEventName();

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener(eventName, handleCustomEvent as EventListener);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener(eventName, handleCustomEvent as EventListener);
    };
  }, []);

  const setDevOption = useCallback((key: string, value: any) => {
    setDeveloperOption(key, value);
    setDevOptionsState(loadDeveloperOptions());
  }, []);

  const replaceAllDevOptions = useCallback((next: DeveloperOptions) => {
    saveDeveloperOptions(next);
    setDevOptionsState(loadDeveloperOptions());
  }, []);

  return {
    devOptions,
    setDevOption,
    replaceAllDevOptions,
  } as const;
}
